import { useNavigate } from "react-router-dom";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { ArrowRight, MapPin, DollarSign, Truck, Calendar } from "lucide-react";

export interface MatchedLoad {
  loadId: string;
  brokerName: string;
  originLocation: string;
  destinationLocation: string;
  originCity?: string | null;
  originState?: string | null;
  destCity?: string | null;
  destState?: string | null;
  rate: number | null;
  miles: number | null;
  equipment?: string | null;
  pickupDate?: string | null;
}

interface MatchedLoadsModalProps {
  open: boolean;
  onClose: () => void;
  matches: MatchedLoad[];
}

export default function MatchedLoadsModal({ open, onClose, matches }: MatchedLoadsModalProps) {
  const navigate = useNavigate();

  const openInSearch = (m: MatchedLoad) => {
    // Use existing supported query params on /search (origin, destination, equipment)
    // confirmed in SearchLoadsPageV2.tsx — this reliably surfaces the matched lane.
    const params = new URLSearchParams();
    if (m.originCity && m.originState) params.set("origin", `${m.originCity}, ${m.originState}`);
    if (m.destCity && m.destState) params.set("destination", `${m.destCity}, ${m.destState}`);
    if (m.equipment) params.set("equipment", m.equipment);
    params.set("source", "atlas-match");
    onClose();
    navigate(`/search?${params.toString()}`);
  };

  const openAllInSearch = () => {
    onClose();
    navigate("/search?source=atlas-match");
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-2xl max-h-[80vh] flex flex-col">
        <DialogHeader>
          <DialogTitle>
            {matches.length} Matched Load{matches.length !== 1 ? "s" : ""}
          </DialogTitle>
          <p className="text-xs text-muted-foreground">
            Loads matching your active trucks. Click any row to open it in Search Loads.
          </p>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto -mx-2 px-2 space-y-1.5">
          {matches.length === 0 ? (
            <div className="py-12 text-center text-sm text-muted-foreground">
              No matches to show.
            </div>
          ) : (
            matches.map((m) => {
              const rpm = m.rate && m.miles && m.miles > 0 ? m.rate / m.miles : null;
              return (
                <button
                  key={m.loadId}
                  onClick={() => openInSearch(m)}
                  className="w-full text-left rounded-md border border-border bg-card hover:bg-accent/40 transition-colors px-3 py-2.5 group"
                >
                  <div className="flex items-center justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 text-[12px] font-semibold text-foreground truncate">
                        <span className="truncate">{m.brokerName}</span>
                        {m.equipment && (
                          <span className="inline-flex items-center gap-1 text-[10px] font-normal text-muted-foreground">
                            <Truck className="h-3 w-3" />
                            {m.equipment}
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-1.5 text-[11px] text-muted-foreground mt-0.5">
                        <MapPin className="h-3 w-3 shrink-0" />
                        <span className="truncate">{m.originLocation}</span>
                        <ArrowRight className="h-3 w-3 shrink-0" />
                        <span className="truncate">{m.destinationLocation}</span>
                      </div>
                      {m.pickupDate && (
                        <div className="flex items-center gap-1 text-[10px] text-muted-foreground mt-0.5">
                          <Calendar className="h-2.5 w-2.5" />
                          <span>Pickup {m.pickupDate}</span>
                          {m.miles ? <span>· {m.miles} mi</span> : null}
                        </div>
                      )}
                    </div>
                    <div className="text-right shrink-0">
                      {m.rate ? (
                        <div className="text-[13px] font-bold text-foreground inline-flex items-center">
                          <DollarSign className="h-3 w-3" />
                          {m.rate.toLocaleString()}
                        </div>
                      ) : (
                        <div className="text-[11px] text-muted-foreground">Call</div>
                      )}
                      {rpm ? (
                        <div className="text-[10px] text-muted-foreground">${rpm.toFixed(2)}/mi</div>
                      ) : null}
                    </div>
                  </div>
                </button>
              );
            })
          )}
        </div>

        <div className="flex items-center justify-between gap-2 pt-2 border-t border-border">
          <Button variant="ghost" size="sm" onClick={onClose}>Close</Button>
          {matches.length > 0 && (
            <Button variant="secondary" size="sm" onClick={openAllInSearch}>
              Open All in Search Loads
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
